/**
 * numbering-preview.ts — pure logika numeracji V2 (F12.I) po stronie frontu.
 *
 * Duplikacja typów + pure functions z `apps/api/lib/custom-modules/numbering.ts`.
 * Dlaczego nie shared package: cleanup do `packages/shared/src/numbering.ts`
 * odłożony — zadanie Boards 1523. Frontend potrzebuje live
 * preview bez round-trip do backendu — pure function lokalnie.
 *
 * **Nigdy nie wywołuj `generateRecordNumber` ani `getAndIncrementCounter` tutaj
 * — to są DB functions z backendu (SELECT FOR UPDATE na counter row).**
 */

import { APP_TIME_ZONE } from '@saas/shared';

// ─── Types (1:1 z lib/custom-modules/numbering.ts) ──────────────────────────

export type ResetPolicy = 'never' | 'yearly' | 'monthly' | 'daily';

export type NumberingSegment =
  | { type: 'text'; value: string }
  | { type: 'separator'; value: string }
  | { type: 'year'; format?: 'YYYY' | 'YY' }
  | { type: 'month' }
  | { type: 'day' }
  | { type: 'quarter' }
  | { type: 'counter'; padding?: number }
  | {
      type: 'field_value';
      fieldSlug: string;
      transform?: 'uppercase' | 'lowercase' | 'first_n';
      firstN?: number;
    }
  | { type: 'conditional_text'; fieldSlug: string; mapping: Record<string, string> };

export interface NumberingConfigV2 {
  enabled: boolean;
  /** Pole modułu (slug) gdzie wpisać numer. Gdy null/undefined → fallback do `record_number`. */
  targetFieldSlug?: string;
  /** Po wygenerowaniu numer w docelowym polu jest readonly. */
  lockAfterGeneration: boolean;
  /** Przy tworzeniu rekordu user może wpisać własny numer. */
  allowManualOverride: boolean;
  segments: NumberingSegment[];
  resetPolicy: ResetPolicy;
  startFrom: number;
  /** Default padding dla counter segmentów bez explicit padding. */
  padding: number;
}

const DEFAULT_TZ = APP_TIME_ZONE;

// ─── Reset key computation (4 polityki, strefa tenanta) ─────────────────────

/**
 * Liczy klucz okresu resetu na podstawie polityki + daty + strefy czasowej.
 * Identyczna logika jak silnik backend (1:1 port). Używana w UI do
 * pokazania user'owi „bieżący okres" przy przycisku reset (np. „2026").
 *
 * Przykłady (date = teraz, tz = Europe/Warsaw):
 *   never   → ''
 *   yearly  → '2026'
 *   monthly → '2026-05'
 *   daily   → '2026-05-21'
 */
export function computeResetKey(
  policy: ResetPolicy,
  date: Date = new Date(),
  tz: string = DEFAULT_TZ,
): string {
  if (policy === 'never') return '';

  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const parts = formatter.formatToParts(date);
  const y = parts.find((p) => p.type === 'year')?.value ?? '';
  const m = parts.find((p) => p.type === 'month')?.value ?? '';
  const d = parts.find((p) => p.type === 'day')?.value ?? '';

  switch (policy) {
    case 'yearly':
      return y;
    case 'monthly':
      return `${y}-${m}`;
    case 'daily':
      return `${y}-${m}-${d}`;
  }
}

// ─── Segment generators (port 1:1 z silnika backendu) ───────────────────────

interface BuildContext {
  date: Date;
  counter: number;
  recordData: Record<string, unknown>;
  tz: string;
  defaultPadding: number;
}

function generateSegment(seg: NumberingSegment, ctx: BuildContext): string {
  switch (seg.type) {
    case 'text':
      return seg.value || '';
    case 'separator':
      return seg.value || '/';
    case 'year': {
      const yearStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: ctx.tz,
        year: 'numeric',
      }).format(ctx.date);
      return seg.format === 'YY' ? yearStr.slice(-2) : yearStr;
    }
    case 'month':
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: ctx.tz,
        month: '2-digit',
      }).format(ctx.date);
    case 'day':
      return new Intl.DateTimeFormat('en-CA', {
        timeZone: ctx.tz,
        day: '2-digit',
      }).format(ctx.date);
    case 'quarter': {
      const monthStr = new Intl.DateTimeFormat('en-CA', {
        timeZone: ctx.tz,
        month: '2-digit',
      }).format(ctx.date);
      const monthNum = parseInt(monthStr, 10);
      const q = Math.floor((monthNum - 1) / 3) + 1;
      return `Q${q}`;
    }
    case 'counter': {
      const padding = Math.min(Math.max(seg.padding ?? ctx.defaultPadding, 1), 10);
      return String(ctx.counter).padStart(padding, '0');
    }
    case 'field_value': {
      let fieldVal = String(ctx.recordData[seg.fieldSlug] ?? '');
      if (seg.transform === 'uppercase') fieldVal = fieldVal.toUpperCase();
      else if (seg.transform === 'lowercase') fieldVal = fieldVal.toLowerCase();
      else if (seg.transform === 'first_n' && seg.firstN != null)
        fieldVal = fieldVal.slice(0, seg.firstN);
      return fieldVal;
    }
    case 'conditional_text': {
      const fieldVal = String(ctx.recordData[seg.fieldSlug] ?? '');
      return seg.mapping[fieldVal] ?? seg.mapping['_default'] ?? '';
    }
  }
}

export function buildNumber(
  segments: NumberingSegment[],
  counter: number,
  recordData: Record<string, unknown> = {},
  date: Date = new Date(),
  tz: string = DEFAULT_TZ,
  defaultPadding: number = 4,
): string {
  const ctx: BuildContext = { date, counter, recordData, tz, defaultPadding };
  return segments.map((s) => generateSegment(s, ctx)).join('');
}

export function previewRecordNumber(
  config: NumberingConfigV2,
  sampleData: Record<string, unknown> = {},
  sampleCounter?: number,
): string {
  const counter = sampleCounter ?? config.startFrom ?? 1;
  return buildNumber(
    config.segments,
    counter,
    sampleData,
    new Date(),
    DEFAULT_TZ,
    config.padding ?? 4,
  );
}

// ─── Quick-start templates (F12.I D9) ───────────────────────────────────────

/**
 * 4 presety segmentów dostępne jako buttony „Szablony" w panelu numeracji.
 * Klik → wypełnia segmenty kategorii. PREFIX jako placeholder do edycji przez admina.
 */
export interface QuickStartTemplate {
  /** Klucz i18n: `tenant.numbering.templates.<key>` */
  key:
    | 'prefixCounter'
    | 'prefixYearCounter'
    | 'prefixYearMonthCounter'
    | 'prefixYearQuarterCounter';
  /** Czytelny opis formatu — używany w button label. */
  label: string;
  /** Domyślne segmenty po kliku. */
  segments: NumberingSegment[];
  /** Domyślna polityka resetu. */
  resetPolicy: ResetPolicy;
}

export const QUICK_START_TEMPLATES: QuickStartTemplate[] = [
  {
    key: 'prefixCounter',
    label: 'PREFIX-NNNN',
    segments: [
      { type: 'text', value: 'PREFIX' },
      { type: 'separator', value: '-' },
      { type: 'counter', padding: 4 },
    ],
    resetPolicy: 'never',
  },
  {
    key: 'prefixYearCounter',
    label: 'PREFIX/RRRR/NNNN',
    segments: [
      { type: 'text', value: 'PREFIX' },
      { type: 'separator', value: '/' },
      { type: 'year', format: 'YYYY' },
      { type: 'separator', value: '/' },
      { type: 'counter', padding: 4 },
    ],
    resetPolicy: 'yearly',
  },
  {
    key: 'prefixYearMonthCounter',
    label: 'PREFIX/RRRR/MM/NNNN',
    segments: [
      { type: 'text', value: 'PREFIX' },
      { type: 'separator', value: '/' },
      { type: 'year', format: 'YYYY' },
      { type: 'separator', value: '/' },
      { type: 'month' },
      { type: 'separator', value: '/' },
      { type: 'counter', padding: 4 },
    ],
    resetPolicy: 'monthly',
  },
  {
    key: 'prefixYearQuarterCounter',
    label: 'PREFIX/RRRR/QN-NNNN',
    segments: [
      { type: 'text', value: 'PREFIX' },
      { type: 'separator', value: '/' },
      { type: 'year', format: 'YYYY' },
      { type: 'separator', value: '/' },
      { type: 'quarter' },
      { type: 'separator', value: '-' },
      { type: 'counter', padding: 4 },
    ],
    resetPolicy: 'yearly',
  },
];

/**
 * Domyślne wartości dla nowo dodanego segmentu danego typu (Add popover).
 * Każdy typ z 9 dostępnych otrzymuje sensowny default żeby panel od razu
 * pokazał coś użytecznego w live preview po dodaniu.
 */
export function defaultSegmentForType(type: NumberingSegment['type']): NumberingSegment {
  switch (type) {
    case 'text':
      return { type: 'text', value: 'PREFIX' };
    case 'separator':
      return { type: 'separator', value: '/' };
    case 'year':
      return { type: 'year', format: 'YYYY' };
    case 'month':
      return { type: 'month' };
    case 'day':
      return { type: 'day' };
    case 'quarter':
      return { type: 'quarter' };
    case 'counter':
      return { type: 'counter', padding: 4 };
    case 'field_value':
      return { type: 'field_value', fieldSlug: '', transform: undefined };
    case 'conditional_text':
      return { type: 'conditional_text', fieldSlug: '', mapping: { _default: '' } };
  }
}

/** Lista 9 typów segmentów w kolejności do wyświetlenia w Add popover. */
export const SEGMENT_TYPES: NumberingSegment['type'][] = [
  'text',
  'separator',
  'year',
  'month',
  'day',
  'quarter',
  'counter',
  'field_value',
  'conditional_text',
];

/** Domyślny config dla świeżo włączonej numeracji (gdy admin klika toggle). */
const DEFAULT_TEMPLATE = QUICK_START_TEMPLATES.find((t) => t.key === 'prefixYearCounter')!;
export const DEFAULT_V2_CONFIG: NumberingConfigV2 = {
  enabled: false,
  targetFieldSlug: undefined,
  lockAfterGeneration: false,
  allowManualOverride: false,
  segments: DEFAULT_TEMPLATE.segments, // PREFIX/RRRR/NNNN — najczęstszy
  resetPolicy: DEFAULT_TEMPLATE.resetPolicy,
  startFrom: 1,
  padding: 4,
};
